interface CacheEntry {
    data: any;
    timestamp: number;
    customerId: string;
}

interface MapDataCache {
    street: CacheEntry | null;
    road: CacheEntry | null;
    inflow: CacheEntry | null;
    stormNodes: CacheEntry | null;
    stormLines: CacheEntry | null;
    sewerNodes: CacheEntry | null;
    sewerLines: CacheEntry | null;
}

class MapDataCacheService {
    private cache: MapDataCache = {
        street: null,
        road: null,
        inflow: null,
        stormNodes: null,
        stormLines: null,
        sewerNodes: null,
        sewerLines: null
    };

    private readonly CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

    private isValidCache(entry: CacheEntry | null, customerId: string): boolean {
        if (!entry) return false;
        if (entry.customerId !== customerId) return false;
        return (Date.now() - entry.timestamp) < this.CACHE_DURATION;
    }

    private createCacheEntry(data: any, customerId: string): CacheEntry {
        return {
            data,
            timestamp: Date.now(),
            customerId
        };
    }

    // Street data
    getStreetData(customerId: string): any | null {
        return this.isValidCache(this.cache.street, customerId) ? this.cache.street!.data : null;
    }

    setStreetData(data: any, customerId: string): void {
        this.cache.street = this.createCacheEntry(data, customerId);
    }

    // Road data
    getRoadData(customerId: string): any | null {
        return this.isValidCache(this.cache.road, customerId) ? this.cache.road!.data : null;
    }

    setRoadData(data: any, customerId: string): void {
        this.cache.road = this.createCacheEntry(data, customerId);
    }

    // Inflow data
    getInflowData(customerId: string): any | null {
        return this.isValidCache(this.cache.inflow, customerId) ? this.cache.inflow!.data : null;
    }

    setInflowData(data: any, customerId: string): void {
        this.cache.inflow = this.createCacheEntry(data, customerId);
    }

    // Storm nodes
    getStormNodesData(customerId: string): any | null {
        return this.isValidCache(this.cache.stormNodes, customerId) ? this.cache.stormNodes!.data : null;
    }

    setStormNodesData(data: any, customerId: string): void {
        this.cache.stormNodes = this.createCacheEntry(data, customerId);
    }

    // Storm lines
    getStormLinesData(customerId: string): any | null {
        return this.isValidCache(this.cache.stormLines, customerId) ? this.cache.stormLines!.data : null;
    }

    setStormLinesData(data: any, customerId: string): void {
        this.cache.stormLines = this.createCacheEntry(data, customerId);
    }

    // Sewer nodes
    getSewerNodesData(customerId: string): any | null {
        return this.isValidCache(this.cache.sewerNodes, customerId) ? this.cache.sewerNodes!.data : null;
    }

    setSewerNodesData(data: any, customerId: string): void {
        this.cache.sewerNodes = this.createCacheEntry(data, customerId);
    }

    // Sewer lines
    getSewerLinesData(customerId: string): any | null {
        return this.isValidCache(this.cache.sewerLines, customerId) ? this.cache.sewerLines!.data : null;
    }

    setSewerLinesData(data: any, customerId: string): void {
        this.cache.sewerLines = this.createCacheEntry(data, customerId);
    }

    // Clear cache for a specific customer
    clearCustomerCache(customerId: string): void {
        Object.keys(this.cache).forEach(key => {
            const entry = this.cache[key as keyof MapDataCache];
            if (entry && entry.customerId === customerId) {
                this.cache[key as keyof MapDataCache] = null;
            }
        });
    }

    // Clear all cache
    clearAllCache(): void {
        this.cache = {
            street: null,
            road: null,
            inflow: null,
            stormNodes: null,
            stormLines: null,
            sewerNodes: null,
            sewerLines: null
        };
    }
}

// Export singleton instance
export const mapDataCache = new MapDataCacheService(); 